import React, { Component, useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import PropTypes from 'prop-types';
import axios from 'axios';
import Image from 'next/image';

// Dynamic imports
const VodacomLogo = dynamic(() =>
  import('../../../../public/assets/logo/VodacomLogo.js')
);
const VodacomBusinessLogo = dynamic(() =>
  import('../../../../public/assets/logo/vodacom-business-logo.js')
);
const Navigation = dynamic(() => import('../Navigation'));
const SearchComponent = dynamic(() => import('../SearchComponent'));

const Icon = dynamic(() => import('../../Icons/Icon'));

const Header = (props) => {
  const s = require('./header.module.scss');
  const [opened, setOpened] = useState(false);
  const [navigation, setNavigation] = useState();
  const [isPodcastOpen, setIsPodcastOpen] = useState(false);
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchNavigation = async () => {
      const response = await axios.get(`/api/navigation`);
      setNavigation(response.data.entities);
    };
    fetchNavigation();

    // Add event listener to close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsPodcastOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchPodcasts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.NEXT_PUBLIC_SITE_URL}/api/${process.env.NEXT_PUBLIC_API_VERSION}/posts/podcast`
      );
      setPodcasts(response.data?.list || []);
    } catch (error) {
      console.error('Error fetching podcasts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePodcastClick = async () => {
    if (!isPodcastOpen && podcasts.length === 0) {
      await fetchPodcasts();
    }
    setIsPodcastOpen(!isPodcastOpen);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')} ${date.toLocaleString(
      'default',
      { month: 'long' }
    )} ${date.getFullYear()}`;
  };

  const { path, toggleSearch, isMenuOpen, terms } = props;

  const podcastSection = navigation?.sections?.find(
    (section) => section.slug === 'podcast'
  );

  return (
    <div className={s.Header}>
      <div className={s.HeaderContent}>
        <div className={s.HeaderContainer}>
          <div className={s.NavSearchContainer}>
            <Navigation
              toggleMenu={() => setOpened(!opened)}
              opened={opened}
              path={path}
              navigation={navigation}
            />
            <SearchComponent
              toggleSearch={toggleSearch}
              isMenuOpen={isMenuOpen}
              terms={terms}
            />
          </div>

          <Link href='/' passHref>
            <a className={s.Logo} aria-label='Vodacom logo'>
              {path.includes('business') ||
              path.includes('category/vodacom-solutions') ||
              path.includes('category/opinion') ||
              path.includes('category/devices') ||
              path.includes('category/case-studies') ||
              path.includes('category/catelogues') ||
              path.includes('category/sme-solutions') ? (
                <VodacomBusinessLogo style={{ maxWidth: '150px' }} />
              ) : (
                <VodacomLogo style={{ width: '252px', height: '29px' }} />
              )}
            </a>
          </Link>
        </div>
        <div className={s.HeaderActions}>
          {podcastSection && (
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <button className={s.PodcastButton} onClick={handlePodcastClick}>
                <div className={s.PodcastIcons}>
                  <Icon
                    className={`${s.icon} ${s.podcastIcon}`}
                    icon='SOCIAL_PODCAST'
                    theme='x'
                    size={16}
                  />
                  Tech Talk with Vodacom...
                  <Icon
                    className={`${s.icon} ${isPodcastOpen ? s.rotated : ''}`}
                    icon='ACTION_CHEVRON_DOWN'
                    theme='x'
                    size={12}
                  />
                </div>
                <div className={s.MobilePodcastIcon}>
                  <Image
                    src='/podcast-desktop.png'
                    alt='Vodacom Logo'
                    width={22}
                    height={27}
                  />
                </div>
              </button>

              <div
                className={`${s.PodcastDropdown} ${
                  isPodcastOpen ? s.open : s.closed
                }`}>
                <div className={s.MobilePodcastHeader}>
                  <div className={s.MobilePodcastLogo}>
                    <Image
                      src='/modal-logo.png'
                      alt='Vodacom Logo'
                      width={50}
                      height={50}
                    />
                    <Image
                      src='/modal-title.png'
                      alt='Vodacom Logo'
                      width={107}
                      height={39}
                    />
                  </div>
                  <button
                    className={s.CloseButton}
                    onClick={() => setIsPodcastOpen(false)}
                    aria-label='Close podcast menu'>
                    ✕
                  </button>
                </div>

                <div className={s.PodcastHeader}>
                  <h3>Latest Podcasts</h3>
                  <Link href='/category/podcast'>
                    <a onClick={() => setIsPodcastOpen(false)}>View All</a>
                  </Link>
                </div>

                <div className={s.PodcastList}>
                  {loading ? (
                    <p>Loading podcasts...</p>
                  ) : podcasts.length > 0 ? (
                    podcasts.slice(0, 4).map((podcast, index) => (
                      <div key={`podcast-${index}`} className={s.PodcastItem}>
                        <div className={s.PodcastTime}>
                          {formatDate(podcast.date)}
                        </div>
                        <div className={s.PodcastTitle}>{podcast.title}</div>

                        <Link href={`/article${podcast.button.link}`}>
                          <a
                            onClick={(e) => {
                              setIsPodcastOpen(false);
                              if (path && path.includes('/article/')) {
                                e.preventDefault();
                                window.location.href = `/article${podcast.button.link}`;
                              }
                            }}
                            className={s.ListenButton}>
                            Read more
                          </a>
                        </Link>
                      </div>
                    ))
                  ) : (
                    <p>No podcasts available</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Header.propTypes = {
  path: PropTypes.string,
  toggleSearch: PropTypes.func,
  isMenuOpen: PropTypes.bool,
  terms: PropTypes.string,
  navigation: PropTypes.object,
};

export default Header;
